import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import config from '../../config';
import ForgotPassword from '../../components/auth/ForgotPassword';
import { fieldError } from '../../lib/validation';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate('/admin', { replace: true }); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const err = fieldError({ email });
    if (err) { setError(err); return; }
    if (!password) { setError('Enter your password'); return; }
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(' ') : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#f5f3f7] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Link
            to="/"
            className="flex flex-col items-center justify-center shrink-0 mb-6"
            style={{ minWidth: '170px' }}
          >
            <img
              src={config.LOGO_URL}
              alt="BookMyJab"
              style={{
                width: '160px',
                minWidth: '140px',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
            <span
              className="text-[9px] md:text-[10px] italic font-medium text-[#311b52]/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Uncompromising Care. Seamlessly Yours.
            </span>
          </Link>
          <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Admin Portal</h1>
          <p className="text-sm text-[#673c96] mt-1" style={{ fontFamily: 'Montserrat' }}>Sign in to manage operations</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-[#673c96]/10">
          {error && (
            <div data-testid="login-error" className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#311b52] mb-1.5" style={{ fontFamily: 'Poppins' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#673c96]" />
                <input
                  data-testid="login-email-input"
                  type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#673c96]/20 focus:border-[#673c96] focus:ring-2 focus:ring-[#673c96]/20 outline-none text-sm transition-all"
                  placeholder="admin@bookmyjab.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#311b52] mb-1.5" style={{ fontFamily: 'Poppins' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#673c96]" />
                <input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-[#673c96]/20 focus:border-[#673c96] focus:ring-2 focus:ring-[#673c96]/20 outline-none text-sm transition-all"
                  placeholder="Enter password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#673c96]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit" disabled={loading}
            className="w-full mt-6 bg-[#f37721] text-white hover:bg-[#d9651a] rounded-xl px-6 py-3 font-semibold text-sm transition-all disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <button type="button" className="w-full mt-3 text-sm text-[#673c96]" onClick={() => setShowForgot(true)}>Forgot password?</button>
          {showForgot && <ForgotPassword onClose={() => setShowForgot(false)} />}
        </form>

        <p className="text-center mt-6 text-xs text-[#673c96]">This portal is for authorized administrators only.</p>
      </div>
    </div>
  );
}
